import React from 'react';

import { useDropdownState } from '../../hooks/useDropdownState';
import { TimeUnit, formatTimeUnit } from '../../utils/timeConversion';

import { MenuButton } from './alert-form-v3';

import { Dropdown, Icon } from '@controlrooms/components';
import { ICONS } from '@controlrooms/constants';

export interface DurationDropdownProps {
  /** Current time unit */
  currentUnit: TimeUnit;
  /** Callback when unit changes */
  onUnitChange: (unit: TimeUnit) => void;
  /** Test ID for the dropdown button */
  testId?: string;
  /** Additional CSS styles */
  style?: React.CSSProperties;
}

export const DurationDropdown: React.FC<DurationDropdownProps> = ({
  currentUnit,
  onUnitChange,
  testId = 'duration-dropdown',
  style,
}) => {
  const dropdownState = useDropdownState();

  const handleUnitSelect = (selectedUnit: TimeUnit) => {
    if (selectedUnit === currentUnit) {
      dropdownState.close();
      return;
    }

    // Simply change the unit - no conversion needed since we always store in minutes
    onUnitChange(selectedUnit);
    dropdownState.close();
  };

  return (
    <Dropdown.Root onClose={dropdownState.close} isOpen={dropdownState.isOpen}>
      <Dropdown.Trigger style={{ padding: 0 }}>
        <MenuButton
          data-testid={testId}
          buttonSize="small"
          buttonType="menu"
          aria-haspopup="listbox"
          onClick={dropdownState.toggle}
          aria-expanded={dropdownState.isOpen}
        >
          {formatTimeUnit(currentUnit)}
        </MenuButton>
      </Dropdown.Trigger>
      <Dropdown.Options
        position="right"
        open={dropdownState.isOpen}
        ref={dropdownState.ref}
        style={{ minWidth: '65px', ...style }}
      >
        <Dropdown.Item onClick={() => handleUnitSelect('mins')}>
          {currentUnit === 'mins' && (
            <Icon data-testid="mins-checkmark" name={ICONS.CheckmarkDefault} />
          )}
          <span style={{ paddingLeft: currentUnit === 'mins' ? 0 : 20 }}>Mins</span>
        </Dropdown.Item>
        <Dropdown.Item onClick={() => handleUnitSelect('hrs')}>
          {currentUnit === 'hrs' && (
            <Icon data-testid="hrs-checkmark" name={ICONS.CheckmarkDefault} />
          )}
          <span style={{ paddingLeft: currentUnit === 'hrs' ? 0 : 20 }}>Hrs</span>
        </Dropdown.Item>
      </Dropdown.Options>
    </Dropdown.Root>
  );
};
